/**
 * The single fetch surface for the app. Nothing else calls fetch directly.
 *
 * Session-ending failures are distinguished from ordinary ones: the backend
 * returns 401 for a dead token, and 403 with a `code` for "you are logged in but
 * must finish something first". Treating those the same is what would log a
 * half-onboarded user straight back out, so they are separate types here.
 */

import { checkForNewVersion } from "@/lib/stale-version";

export type ApiErrorCode = "MUST_RESET_PASSWORD" | "MUST_COMPLETE_ONBOARDING";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | undefined;
  readonly payload: unknown;

  constructor(message: string, status: number, code?: ApiErrorCode, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  /** A dead or missing token: the session is over. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /** Logged in, but gated behind password reset or onboarding. */
  get isGated() {
    return this.status === 403 && this.code !== undefined;
  }
}

/**
 * Every path in this app is written same-origin (`/api/...`), which is what the
 * Vite dev proxy and the Express-served bundle both rely on. The standalone
 * frontend service has no API behind it, so it builds with `VITE_API_URL` set to
 * the backend's origin and every request is rewritten to point there.
 *
 * Empty by default, so `endpoint("/api/x")` is `"/api/x"` and the same-origin
 * deployments are untouched. Trailing slash is stripped so the two halves can't
 * join into `//api`.
 */
const API_ORIGIN = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

function endpoint(path: string): string {
  return `${API_ORIGIN}${path}`;
}

const TOKEN_KEY = "mentoria_token";

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable (private mode). The in-memory session still works
    // for this tab; the user simply has to log in again on reload.
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** FormData bypasses JSON encoding so the browser sets the multipart boundary. */
  formData?: FormData;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, formData, signal } = options;
  const token = readToken();

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const init: RequestInit = { method, headers };
  if (formData) init.body = formData;
  else if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;

  let response: Response;
  try {
    response = await fetch(endpoint(path), init);
  } catch {
    throw new ApiError("Could not reach the server. Check that the backend is running.", 0);
  }

  // 204 and empty bodies are valid successes.
  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    // A rejected request is the moment a stale tab is most likely to reveal
    // itself (an old payload against a new contract), so use it as a trigger
    // rather than waiting for the next scheduled check. Fire-and-forget: the
    // error below must throw regardless of what the version probe learns.
    void checkForNewVersion();
    const record = (payload ?? {}) as { error?: string; code?: ApiErrorCode };
    throw new ApiError(
      record.error || `Request failed (${response.status}).`,
      response.status,
      record.code,
      payload,
    );
  }

  return payload as T;
}

/** Builds a query string, dropping undefined values. */
export function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/**
 * Documents are streamed by the backend behind a bearer token, so they can't be
 * loaded with a plain <a href>. Fetching to a blob URL is what lets an
 * authenticated file open in a new tab.
 */
export async function fetchFileBlobUrl(path: string): Promise<string> {
  const token = readToken();
  const response = await fetch(endpoint(path), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let message = "Could not open that document.";
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Non-JSON error body; the default message stands.
    }
    throw new ApiError(message, response.status);
  }
  return URL.createObjectURL(await response.blob());
}
